import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { warehouses } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

const STATUSES = ['Available', 'Reserved', 'Shipped', 'In Repair'];

/**
 * @param {{
 *   open: boolean,
 *   mode: 'add'|'edit',
 *   partNumber: string,
 *   serial: object|null,
 *   onClose: () => void,
 *   onSave: (values: { serialNumber: string, locationName: string, status: string }) => void,
 * }} props
 */
export default function EditSerialModal({ open, mode, partNumber, serial, onClose, onSave }) {
  const [serialNumber, setSerialNumber] = useState('');
  const [locationName, setLocationName] = useState('');
  const [status,       setStatus]       = useState('Available');
  const [ticketNumber, setTicketNumber] = useState('');

  const locationOptions = warehouses.filter((w) => w.active).map((w) => w.name);
  const needsTicket = status === 'Shipped' || status === 'Reserved';

  useEffect(() => {
    if (open) {
      setSerialNumber(serial?.serialNumber ?? '');
      setLocationName(serial?.locationName ?? (locationOptions[0] ?? ''));
      setStatus(serial?.status ?? 'Available');
      setTicketNumber(serial?.ticketNumber ?? '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    onSave({
      serialNumber: serialNumber.trim(),
      locationName,
      status,
      ticketNumber: needsTicket ? (ticketNumber.trim() || null) : null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            {mode === 'add' ? 'Add Serial Number' : 'Edit Serial Number'}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
            {partNumber}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: '-2px' }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField
          label="Serial Number"
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          size="small"
          fullWidth
          required
          placeholder="e.g. SN-PWR-015"
          sx={fieldSx}
        />

        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel>Location</InputLabel>
          <Select
            label="Location"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
          >
            {locationOptions.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontSize: '0.875rem' }}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.875rem' }}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsTicket && (
          <TextField
            label="Ticket #"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g. INC10041"
            sx={fieldSx}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!serialNumber.trim() || !locationName}
          sx={{ backgroundColor: '#009688',
            fontSize: '0.875rem',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#00695c', boxShadow: 'none' },
            '&:disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
